/** 숫자형 병원 상세 URL과 폐기된 pretty URL의 경계를 정의한다. */

export const HOSPITAL_DETAIL_IMAGES_SEGMENT = "images"

export type HospitalDetailSubPage = "images"

export type HospitalDetailPathInput = {
  locale: string
  hospitalOriginId: number | string
  /** @deprecated pretty URL 라벨은 숫자형 정본에서 무시한다. */
  regionName?: string
  /** @deprecated pretty URL 라벨은 숫자형 정본에서 무시한다. */
  treatmentName?: string
  sub?: HospitalDetailSubPage
}

/** 병원 상세의 유일한 정본 경로를 만든다. */
export const buildHospitalShortPath = (
  locale: string,
  hospitalOriginId: number | string,
): string => `/${locale}/hospital/${hospitalOriginId}`

export const buildHospitalDetailPath = ({
  locale,
  hospitalOriginId,
  sub,
}: HospitalDetailPathInput): string => {
  const base = buildHospitalShortPath(locale, hospitalOriginId)
  return sub === "images" ? `${base}/${HOSPITAL_DETAIL_IMAGES_SEGMENT}` : base
}

/**
 * 숫자형 상세가 metadata·JSON-LD에 쓰는 로케일 제외 경로(`hospital/{id}`).
 * `buildLocalizedPageMetadata`·`localizedUrl` 이 로케일을 앞에 붙인다.
 */
export const buildHospitalDetailSeoPath = (
  input: Omit<HospitalDetailPathInput, "locale">,
): string => buildHospitalDetailPath({ ...input, locale: "ko" }).replace("/ko/", "")

const HOSPITAL_PRETTY_PATH_PATTERN =
  /^\/(ko|en|cn|jp)\/hospital\/[^/]+\/[^/]+\/(\d+)(\/images)?\/?$/

/**
 * 폐기된 pretty URL을 숫자형 정본으로 바꾼다.
 *
 * 이 함수는 요청 경로의 세그먼트만 읽는다. 지역·진료과 taxonomy나 병원 API를 호출하지
 * 않으므로 잘못된 slug·taxonomy 장애와 무관하게 한 hop 목적지를 계산한다.
 * 숫자 ID가 0 또는 안전한 정수가 아니면 숫자형 상세 라우트의 기존 404 흐름을 유지한다.
 */
export const resolveHospitalPrettyRedirect = (pathname: string): string | null => {
  const matched = pathname.match(HOSPITAL_PRETTY_PATH_PATTERN)
  if (!matched) return null

  const [, locale, rawHospitalId, rawSubPath] = matched
  const hospitalId = Number(rawHospitalId)
  if (!Number.isSafeInteger(hospitalId) || hospitalId <= 0) return null

  return `/${locale}/hospital/${hospitalId}${rawSubPath ?? ""}`
}

/** 숫자형 상세의 사진 페이지. */
export const buildHospitalImagesPath = (
  locale: string,
  hospitalOriginId: number | string,
): string => `${buildHospitalShortPath(locale, hospitalOriginId)}/images`

/**
 * 기존 pretty 호출부와의 호환을 위한 하위 경로 builder.
 * 인자는 남겨 두지만 정본은 숫자형 하나이므로 지역·진료과 세그먼트는 무시한다.
 */
export const buildHospitalDetailPathFromSegments = ({
  locale,
  hospitalOriginId,
  sub,
}: {
  locale: string
  regionSegment?: string
  treatmentSegment?: string
  hospitalOriginId: number | string
  sub?: HospitalDetailSubPage
}): string => buildHospitalDetailPath({ locale, hospitalOriginId, sub })
